import React from 'react';
import { clsx } from 'clsx';
import { formatDecimalParts } from '@/utils/decimal-display';
import { FIGURE_TIER_CLASS, FIGURE_TONE_CLASS, type FigureTier } from './figure-styles';

interface SignedValueProps {
  /** Signed backend Decimal STRING (HON-03). A JS number is coerced with no float math. */
  value: string | number | null | undefined;
  currency?: string;
  dp?: number;
  /** Demoted unit tail ('/sh', '/day'). */
  unit?: string;
  tier?: FigureTier;
  /** Colored +/- by direction (default). false → neutral fg, glyph only. */
  colored?: boolean;
  demoteDecimals?: boolean;
  emptyDisplay?: React.ReactNode;
  className?: string;
}

/**
 * A signed figure: a `+`/`−` glyph (weight 500, .lr-sgn) with green/red color
 * reinforcing direction — NO ▲/▼ arrows (DESIGN-SPEC §2). Sign and tone derive
 * from the ROUNDED magnitude (`formatDecimalParts`), not the raw string, so a
 * value that rounds to zero shows no sign and is not painted red/green.
 */
export function SignedValue({
  value,
  currency,
  dp,
  unit,
  tier = 'body',
  colored = true,
  demoteDecimals = true,
  emptyDisplay = '—',
  className,
}: SignedValueProps) {
  const parts = formatDecimalParts(value, { currency, dp });

  if (parts.isEmpty) {
    return (
      <span className={clsx('lr-num', FIGURE_TIER_CLASS[tier], 'text-lr-muted', className)}>
        {emptyDisplay}
      </span>
    );
  }

  const isZero =
    /^0+$/.test(parts.intGrouped.replace(/,/g, '')) && /^0*$/.test(parts.frac);
  const glyph = parts.negative ? '−' : isZero ? '' : '+';
  const toneClass = !colored
    ? FIGURE_TONE_CLASS.default
    : parts.negative
      ? FIGURE_TONE_CLASS.neg
      : isZero
        ? FIGURE_TONE_CLASS.muted
        : FIGURE_TONE_CLASS.pos;

  const head = `${parts.currencySymbol}${parts.intGrouped}`;
  const fracStr = parts.dp > 0 ? `.${parts.frac}` : '';

  return (
    <span className={clsx('lr-num', FIGURE_TIER_CLASS[tier], toneClass, className)}>
      {glyph && <span className="lr-sgn">{glyph}</span>}
      {head}
      {demoteDecimals ? (
        (fracStr || unit) && (
          <span className="lr-dec">
            {fracStr}
            {unit}
          </span>
        )
      ) : (
        <>
          {fracStr}
          {unit && <span className="lr-dec">{unit}</span>}
        </>
      )}
    </span>
  );
}
